/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import React from 'react';
import { LuisMigratedAppStore } from 'src/clu/core/stores/LuisMigratedAppStore';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useImperativeProgressTracker } from 'src/core/hooks/useImperativeProgressTracker';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';

export const useLuisToCluMigratedApps = (selectedApplications: LuisMigratedAppStore[]) => {
    const { rootStore, dispatcher } = useGlobalState();
    const { createTracker } = useImperativeProgressTracker();
    const { luisAppStoreHouse, cluMigrationStoreHouse } = rootStore;
    const { luisMigratedAppsStoreHouse } = cluMigrationStoreHouse;

    const luisAppsTracker = useProgressTracker({
        persistOnComplete: true,
        onComplete: () => {
            const apps: LuisMigratedAppStore[] = [];
            luisAppStoreHouse.items.map((app: LuisAppStore) => {
                apps.push(
                    new LuisMigratedAppStore(app.id, {
                        luisApp: app,
                        name: app.name,
                        culture: app.culture,
                        selectedVersion: getAppSelectedVersion(app),
                        loadedVersions: getAppLoadedVersions(app)
                    })
                );
            });
            luisMigratedAppsStoreHouse.setItems(apps);
        }
    });

    const getAppSelectedVersion = (application: LuisAppStore) => {
        const selectedApp = selectedApplications.find(app => app.id === application.id);

        if (selectedApp) {
            return selectedApp.selectedVersion;
        }

        return application.activeVersion;
    };

    const getAppLoadedVersions = (application: LuisAppStore) => {
        const selectedApp = selectedApplications.find(app => app.id === application.id);

        if (selectedApp) {
            return [...selectedApp.loadedVersions];
        }

        return [];
    };

    React.useEffect(() => {
        dispatcher.dispatch('loadLuisApps', { tracker: luisAppsTracker });
    }, []);

    const createDataTracker = (appId: string, versionId: string) =>
        createTracker({
            onRunning: () => {
                luisMigratedAppsStoreHouse.findById(appId).setIsDataLoading(true);
            },
            onDone: () => {
                const appVersions = luisMigratedAppsStoreHouse.findById(appId).loadedVersions;
                luisMigratedAppsStoreHouse.findById(appId).setLoadedVersions([...appVersions, versionId]);
                luisMigratedAppsStoreHouse.findById(appId).setIsDataLoading(false);
            }
        });

    const loadAppVersionData = (appId: string, versionId: string) => {
        dispatcher.dispatch('loadAppVersionData', {
            context: { appId, versionId },
            tracker: createDataTracker(appId, versionId)
        });
    };

    const createVersionsTracker = (appId: string) =>
        createTracker({
            onRunning: () => {
                luisMigratedAppsStoreHouse.findById(appId).setAreVersionsLoading(true);
            },
            onDone: () => {
                luisMigratedAppsStoreHouse.findById(appId).setAreVersionsLoading(false);
            }
        });

    const loadAppVersions = (appId: string) =>
        dispatcher.dispatch('loadAppVersions', { context: { appId }, tracker: createVersionsTracker(appId) });

    const setAppSelectedVersion = (appId: string, versionId: string) =>
        luisMigratedAppsStoreHouse.findById(appId).setSelectedVersion(versionId);

    const isLoading = () =>
        luisMigratedAppsStoreHouse.items.some((app: LuisMigratedAppStore) => app.isDataLoading || app.areVersionsLoading);

    return { loadAppVersionData, loadAppVersions, setAppSelectedVersion, isLoading, luisAppsTracker };
};
